import React, { useState } from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { submitOwnerPayout } from '../../services/walletApi';
import {
  TreasuryUltraAlert,
  TreasuryUltraBtn,
  TreasuryUltraField,
  TreasuryUltraInput,
  TreasuryUltraSelect,
  TreasuryUltraShell,
} from './TreasuryUltraShell';

export default function OwnerPayoutForm({ owners = [], onPaid }) {
  const [ownerId, setOwnerId] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const ownerList = (owners || []).filter((u) => u.role === 'owner');
  const selectedOwner = ownerList.find((o) => o.id === ownerId);
  const scrollable = ownerList.length > 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!ownerId || !Number.isFinite(parsedAmount) || parsedAmount < 1) {
      setError('Select owner and enter a valid amount.');
      return;
    }
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const result = await submitOwnerPayout({
        ownerId,
        amount: parsedAmount,
        reference: reference.trim() || `PAYOUT-${Date.now()}`,
        note: note.trim() || undefined,
      });
      if (result.pendingSecondApproval) {
        setMessage(`Payout queued (≥ ₹${Number(result.thresholdInr || 10000).toLocaleString('en-IN')}). A different admin must approve in Maker–Checker.`);
        setAmount('');
        setReference('');
        setNote('');
      } else if (result.duplicate) {
        setMessage('This payout reference was already recorded.');
      } else {
        setMessage(
          `Credited ₹${parsedAmount.toLocaleString('en-IN')} to owner. Treasury balance ₹${Number(result.treasuryBalance || 0).toLocaleString('en-IN')}.`,
        );
        setAmount('');
        setReference('');
        setNote('');
        if (onPaid) onPaid();
      }
    } catch (err) {
      setError(err.message || 'Payout failed — treasury balance may be low.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <TreasuryUltraShell
      title="Treasury → Owner Payout"
      subtitle="Manual settlement: treasury debit → owner Payout Wallet credit"
      icon={String.fromCodePoint(0x1F4B8)}
      accent="payout"
      badge={ownerList.length ? `${ownerList.length} owners` : '0'}
      scrollable={scrollable}
      scrollMaxHeightClass="max-h-[min(400px,50vh)]"
      showScrollHint={scrollable}
      scrollHint="Scroll owner list"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TreasuryUltraAlert tone="info">
          Use NEFT / RTGS UTR as reference for idempotency. Treasury balance must cover the payout amount.
        </TreasuryUltraAlert>

        <div className="grid gap-3 sm:grid-cols-2">
          <TreasuryUltraField label="Owner">
            <TreasuryUltraSelect
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              required
            >
              <option value="">Select owner…</option>
              {ownerList.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || o.email}
                  {o.wallet_balance != null ? ` · wallet ₹${Number(o.wallet_balance).toLocaleString('en-IN')}` : ''}
                </option>
              ))}
            </TreasuryUltraSelect>
          </TreasuryUltraField>

          <TreasuryUltraField label="Amount (INR)">
            <TreasuryUltraInput
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="86100"
              required
            />
          </TreasuryUltraField>

          <TreasuryUltraField label="Reference / UTR">
            <TreasuryUltraInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="NEFT-UTR-…"
              maxLength={64}
            />
          </TreasuryUltraField>

          <TreasuryUltraField label="Note" hint="Optional settlement note">
            <TreasuryUltraInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="BK-… settlement"
              maxLength={200}
            />
          </TreasuryUltraField>
        </div>

        {selectedOwner && amount ? (
          <div className="de-treasury-ultra-preview">
            Transfer{' '}
            <strong className="text-emerald-300">₹{Number(amount).toLocaleString('en-IN')}</strong>
            {' '}from treasury → {selectedOwner.name || selectedOwner.email}
          </div>
        ) : null}

        {error ? <TreasuryUltraAlert tone="error">{error}</TreasuryUltraAlert> : null}
        {message ? <TreasuryUltraAlert tone="success">{message}</TreasuryUltraAlert> : null}

        <TreasuryUltraBtn
          variant="payout"
          type="submit"
          disabled={busy || !ownerId}
          className="w-full sm:w-auto"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
          {busy ? 'Transferring…' : 'Transfer treasury → owner'}
        </TreasuryUltraBtn>
      </form>
    </TreasuryUltraShell>
  );
}
